import { Badge, Card, MantineColor, Stack, Text } from "@mantine/core";
import { useMediaQuery } from "@mantine/hooks";
import { CheckCircleIcon, ClockIcon, IconProps } from "@phosphor-icons/react";
import dayjs from "dayjs";
import { ComponentProps } from "react";

import { omitClassNameFromProps } from "@/utils";
import { cn } from "@/utils/cn";

type TextColorLevels = 50 | 100 | 200 | 300 | 400 | 500 | 600 | 700 | 800 | 900 | 950;
type ExpeditionDateProps = {
  date?: string | Date;
  color?: `text-${string}` | `text-${string}-${TextColorLevels}` | `text-[#${string}]`;
  slots?: {
    wrapper?: ComponentProps<"div">;
    date?: ComponentProps<"p"> & { label?: string };
    time?: ComponentProps<"p"> & { label?: string };
  };
};

export function ExpeditionDate({ date, color, slots }: ExpeditionDateProps) {
  const formattedDate = dayjs(date).format("DD/MM");
  const formattedTime = dayjs(date).format("HH:mm");

  const formattedContent = (type: "date" | "time") => {
    const isValidDate = dayjs(date).isValid();

    if (!isValidDate) {
      return "Data inválida";
    }

    if (type === "date") {
      return slots?.date?.label || formattedDate;
    }
    return slots?.time?.label || formattedTime;
  };

  return (
    <div className={cn("h-fit rounded-sm border-2 border-current text-gray-700", slots?.wrapper?.className, color)} {...omitClassNameFromProps(slots?.wrapper ?? {})}>
      <div className="bg-current px-2 py-0.5">
        <p className={cn("font-semibold text-white", slots?.date?.className)} {...omitClassNameFromProps(slots?.date ?? {})}>
          {slots?.date?.label || formattedContent("date")}
        </p>
      </div>

      <p className={cn("pb-0.5 text-center", slots?.time?.className)} {...omitClassNameFromProps(slots?.time ?? {})}>
        {slots?.time?.label || formattedContent("time")}
      </p>
    </div>
  );
}

type ExpeditionDateFullProps = ExpeditionDateProps & {
  attended_at?: string | Date;
  attendanceStartedAt?: string | Date;
};
export function ExpeditionDateFull({ date, attended_at, attendanceStartedAt, color, slots }: ExpeditionDateFullProps) {
  const isMobile = useMediaQuery("(max-width: 768px)");

  const formattedPredictedDate = dayjs(date).format(isMobile ? "DD/MM" : "DD/MM/YYYY");
  const formattedPredictedDateTime = dayjs(date).format("HH:mm");

  const predictionDate = date ? dayjs(date).format("HH:mm") : null;
  const attendedAtDate = attended_at ? dayjs(attended_at).format("HH:mm") : null;
  const attendanceStartedAtDate = attendanceStartedAt ? dayjs(attendanceStartedAt).format("HH:mm") : null;

  const diffBetweenPredictionAndAttendanceStartedAt =
    predictionDate && attendanceStartedAtDate ? dayjs(attendanceStartedAtDate, "HH:mm").diff(dayjs(predictionDate, "HH:mm"), "minute") : null;

  const sameMinute = diffBetweenPredictionAndAttendanceStartedAt === 0;
  const isAttendedEarly = diffBetweenPredictionAndAttendanceStartedAt !== null && diffBetweenPredictionAndAttendanceStartedAt < 0;
  const isAttendedLate = diffBetweenPredictionAndAttendanceStartedAt !== null && diffBetweenPredictionAndAttendanceStartedAt > 0;

  const getDiffText = (): string => {
    if (sameMinute) {
      return "No horário";
    }

    if (isAttendedEarly) {
      return `-${Math.abs(diffBetweenPredictionAndAttendanceStartedAt)} min ${!isMobile ? "adiantado" : ""}`;
    }

    if (isAttendedLate) {
      return `+${diffBetweenPredictionAndAttendanceStartedAt} min ${!isMobile ? "de atraso" : ""}`;
    }

    return "";
  };
  const diff = getDiffText();

  const isPast = isAttendedEarly && !sameMinute;

  const formattedContent = (type: "date" | "time") => {
    const isValidDate = dayjs(date).isValid();

    if (!isValidDate) {
      return "Data inválida";
    }

    if (type === "date") {
      return slots?.date?.label || formattedPredictedDate;
    }
    return slots?.time?.label || formattedPredictedDateTime;
  };

  return (
    <Card
      className={cn("h-fit! w-full! border-2! border-current! text-gray-700!", slots?.wrapper?.className, color)}
      p={0}
      {...omitClassNameFromProps(slots?.wrapper ?? {})}
      withBorder
    >
      <Stack gap={0}>
        <div className="bg-current px-2 py-0.5">
          <Text ta="center" fw="bold" c="white" className={cn(slots?.date?.className)} {...omitClassNameFromProps(slots?.date ?? {})}>
            {slots?.date?.label || formattedContent("date")}
          </Text>
        </div>

        <div className="grid auto-rows-auto grid-cols-1 md:grid-cols-2">
          <div className="border-r-[0.5px] border-b-[0.5px] border-dashed border-gray-300 px-3 pb-1 md:border-r-[0.5px] md:border-b-0">
            <AttendanceInfo Icon={ClockIcon} c="blue" time={predictionDate} label="Previsão" onReplaceLabel={(label) => (label.length > 4 ? label.slice(0, 4) + "." : label)} />
          </div>

          <div className="border-t-[0.5px] border-l-[0.5px] border-dashed border-gray-300 px-3 pb-1 md:border-t-0 md:border-l-[0.5px]">
            <AttendanceInfo
              Icon={CheckCircleIcon}
              c="green.7"
              time={attendanceStartedAtDate}
              label="Atendimento"
              onReplaceLabel={(label) => (label.length > 5 ? label.slice(0, 5) + "." : label)}
            />
          </div>

          <div className="col-span-1 flex items-center justify-center border-gray-300 pb-1 text-center md:col-span-2 md:border-t md:py-1">
            {!isMobile && (
              <Text c={isPast ? "green" : "red"} fw="bold" lh="normal" size="xs">
                {diff}
              </Text>
            )}

            {isMobile && (
              <Badge variant="light" radius="sm" color={isPast ? "green.7" : "red"}>
                {diff}
              </Badge>
            )}
          </div>
        </div>
      </Stack>
    </Card>
  );
}

type AttendanceInfoProps = {
  Icon: React.ElementType<IconProps>;
  c: MantineColor;
  time: string | null;
  label: string;
  onReplaceLabel?: (label: AttendanceInfoProps["label"]) => React.ReactNode | string;
};
function AttendanceInfo({ Icon, c, time, label, onReplaceLabel }: AttendanceInfoProps) {
  const isMobile = useMediaQuery("(max-width: 768px)");

  const splitLabel = onReplaceLabel && isMobile ? onReplaceLabel(label) : label;

  return (
    <Text c={c} ta="center" className="">
      {!isMobile && (
        <>
          <Text size="xs" fw="bold" lh="normal" span>
            {splitLabel}
          </Text>

          <Text size="xs" lh="normal" className="flex items-center justify-center gap-1">
            <Icon weight="light" />
            {time}
          </Text>
        </>
      )}

      {isMobile && (
        <div className="flex items-center justify-center gap-1 py-1">
          <Icon weight="bold" />

          <Text size="xs" fw="bold" lh="normal">
            {splitLabel}
          </Text>

          <Text size="xs" lh="normal">
            {time}
          </Text>
        </div>
      )}
    </Text>
  );
}
